import classNames from 'classnames';
import {
  AnimationSuccess,
  Button,
  FlexContainer,
  Modal,
  PartialWidthContainer,
  ToDoHeading,
  ToDoItem,
  ToDoScreenBase,
  Txt,
  Icon,
} from 'components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenancy } from 'state';
import {
  TodoItemAgreement,
  TodoItemLinkTenants,
  TodoItemOrdersSetup,
  TodoItemPropertyOccupied,
  TodoItemTenancyPaidDeposit,
  TodoItemTenancyDepositRegistration,
  TodoItemTenancyDepositProtection,
} from './bricks';
import styles from './PropertyTenancyTodoScreen.module.scss';

const items = {
  documents: {
    tenancyAgreement: {
      title: 'Tenancy Agreement',
      component: TodoItemAgreement,
      hasChecked: (tenancy) => tenancy.agreementSigned,
    },
  },
  tenants: {
    linked: {
      label: 'Linked to Propa',
      title: 'Tenants Linked',
      component: TodoItemLinkTenants,
      hasChecked: (tenancy) => tenancy.tenantsLinked,
    },
    paidTheirDeposit: {
      label: 'Paid their Deposit',
      title: 'Tenant Deposit Payment',
      component: TodoItemTenancyPaidDeposit,
      hasChecked: (tenancy) => tenancy.tenantsDepositPaid,
    },
    standingOrdersSetup: {
      label: 'Standing Orders are Setup',
      title: 'Standing Orders Setup',
      component: TodoItemOrdersSetup,
      hasChecked: (tenancy) => tenancy.tenantsStandingOrdersSetup,
    },
  },
  deposit: {
    tenancyDepositRegistered: {
      title: 'Deposit Registration',
      component: TodoItemTenancyDepositRegistration,
      hasChecked: (tenancy) => tenancy.depositRegistered,
    },
    tenancyDepositProtected: {
      title: 'Deposit Protection',
      component: TodoItemTenancyDepositProtection,
      hasChecked: (tenancy) => tenancy.depositProtected,
    },
  },
  property: {
    propertyIsOccupied: {
      title: 'Property is Occupied',
      component: TodoItemPropertyOccupied,
      hasChecked: (tenancy) => tenancy.propertyIsOccupied,
    },
  },
};

const groupColors = {
  documents: 'green',
  tenants: 'blue',
  deposit: 'green',
  property: 'yellow',
};

/**
 * PropertyTenancyTodoScreen
 *
 * @return {JSX.Element}
 */
const PropertyTenancyTodoScreen = ({ className }) => {
  const tenancyDoc = useTenancy();
  // const tenancyData = tenancyDoc.data;
  const [activeTodo, setActiveTodo] = useState(null);

  const navigate = useNavigate();

  const handleBack = () => navigate('../main');
  // const handleChange = (group, key) => () => todoState.toggle(group, key);

  const handleOpen = (group, key) => () => {
    const item = items[group]?.[key];
    if (item) {
      setActiveTodo(item);
    }
  };

  const handleClose = () => setActiveTodo(null);

  const renderGroup = (groupKey) => {
    return Object.keys(items[groupKey]).map((key) => {
      const { title, label, hasChecked } = items[groupKey][key];

      const props = {
        key,
        label: label || title,
        checked: hasChecked?.(tenancyDoc),
        onClick: handleOpen(groupKey, key),
        [groupColors[groupKey]]: true,
      };

      return <ToDoItem {...props} />;
    });
  };

  return (
    <PartialWidthContainer
      className={classNames(
        styles.PropertyTenancyTodoScreen,
        className,
        PropertyTenancyTodoScreen.displayName
      )}
      small
      column
      gap
    >
      <FlexContainer>
        <Button onClick={handleBack}>
          <Icon s name="goback" /> Back to tenancy
        </Button>
      </FlexContainer>
      <ToDoHeading
        title="Todo List"
        description1="Tap on the task to view a guide and information on how to complete."
        description2="When all of these tasks are complete this tenancy will be fully compliant with renting law."
      />
      <FlexContainer column gap>
        <Txt xx>Legal</Txt>
        {renderGroup('documents')}
      </FlexContainer>
      <FlexContainer column gap>
        <Txt xx>Tenants</Txt>
        {renderGroup('tenants')}
      </FlexContainer>
      <FlexContainer column gap>
        <Txt xx>Deposit</Txt>
        {renderGroup('deposit')}
      </FlexContainer>
      <FlexContainer column gap>
        <Txt xx>Property</Txt>
        {renderGroup('property')}
      </FlexContainer>
      {activeTodo ? (
        <Modal onClose={handleClose} title={activeTodo.title} showCloseButton>
          {activeTodo.animated ? <AnimationSuccess /> : null}
          <ToDoScreenBase>
            <activeTodo.component onSuccess={handleClose} />
          </ToDoScreenBase>
        </Modal>
      ) : null}
    </PartialWidthContainer>
  );
};

PropertyTenancyTodoScreen.displayName = 'PropertyTenancyTodoScreen';

export default observer(PropertyTenancyTodoScreen);
